/**
 * Recently opened scans and the last tab used in each (DEC-194 decisions 1 and 3).
 *
 * Remembered per browser, keyed by the signed-in user, in localStorage. Up to 3 scans,
 * newest first. Opening a scan from elsewhere (the Scans page, a link, a reload) puts it
 * first and drops the oldest past 3. Clicking an item of the list itself, or moving
 * between the tabs of the scan already open, updates it where it is and never moves it,
 * so the list does not shift under the pointer. Each item keeps a snapshot (name,
 * source, status) so the sidebar draws it at once, without waiting for the scan to load.
 *
 * Storage can be missing or throw (private mode, blocked site data), so every read and
 * write is guarded and the list is simply empty then.
 */
import { useMemo, useSyncExternalStore } from 'react'
import type { AuthClaims } from '../../api/client'
import type { ScanNavStatus, ScanTabId } from './scanTabs'

export const RECENT_MAX = 3

export interface RecentScan {
  id: string
  repo: string
  source: string
  status: ScanNavStatus | null
}

export interface RecentState {
  items: RecentScan[]
  lastTab: Record<string, ScanTabId>
}

const EMPTY: RecentState = { items: [], lastTab: {} }
const PREFIX = 'tdia.recent.v1.'

/** The storage key for this user. An operator token has no user id, so it is keyed by
 *  its workspace. Null before anyone is signed in. */
export function recentKey(claims: Pick<AuthClaims, 'user_id' | 'tenant_id'> | null): string | null {
  if (!claims) return null
  return PREFIX + (claims.user_id ? `user:${claims.user_id}` : `workspace:${claims.tenant_id}`)
}

const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(PREFIX)) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

function readRaw(key: string | null): string | null {
  if (!key) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function parse(raw: string | null): RecentState {
  if (!raw) return EMPTY
  try {
    const v = JSON.parse(raw) as Partial<RecentState>
    const items = Array.isArray(v.items)
      ? v.items.filter((i): i is RecentScan => !!i && typeof i.id === 'string' && typeof i.repo === 'string').slice(0, RECENT_MAX)
      : []
    const lastTab = v.lastTab && typeof v.lastTab === 'object' ? v.lastTab : {}
    return { items, lastTab }
  } catch {
    return EMPTY
  }
}

export function readRecent(key: string | null): RecentState {
  return parse(readRaw(key))
}

function write(key: string, state: RecentState) {
  // Keep a last tab only for scans still in the list, so the entry stays small.
  const ids = new Set(state.items.map((i) => i.id))
  const lastTab: Record<string, ScanTabId> = {}
  for (const [id, tab] of Object.entries(state.lastTab)) if (ids.has(id)) lastTab[id] = tab
  try {
    window.localStorage.setItem(key, JSON.stringify({ items: state.items, lastTab }))
  } catch {
    // Storage unavailable: the list stays as it was. Nothing else depends on it.
  }
  emit()
}

/**
 * A visit to a scan page. A new scan is added first once its snapshot is known. An
 * existing one is updated where it is, or moved first with `promote` (a scan opened
 * from elsewhere); neither needs the scan to have loaded.
 */
export function recordVisit(
  key: string | null,
  scanId: string,
  tab: ScanTabId | null,
  snapshot?: Omit<RecentScan, 'id'>,
  promote = false,
) {
  if (!key) return
  const state = readRecent(key)
  const at = state.items.findIndex((i) => i.id === scanId)
  if (!snapshot && at < 0) return
  const item = snapshot ? { id: scanId, ...snapshot } : state.items[at]
  const items =
    at >= 0 && !promote
      ? state.items.map((i, n) => (n === at ? item : i))
      : [item, ...state.items.filter((i) => i.id !== scanId)].slice(0, RECENT_MAX)
  const lastTab = tab ? { ...state.lastTab, [scanId]: tab } : state.lastTab
  const next = { items, lastTab }
  if (JSON.stringify(next) === JSON.stringify(state)) return
  write(key, next)
}

/**
 * Which scan this browser tab is on, so a visit can tell "opened from elsewhere" from
 * "still in the same scan" (a tab change, a reload of the data, a link inside the page).
 * Cleared by any page that is not a scan page.
 */
let openScan: string | null = null

/** True the first time a scan page renders since the reader was on another scan or on
 *  a page outside any scan; false while they stay in the same scan. */
export function enterScan(scanId: string | null): boolean {
  const entering = scanId !== null && scanId !== openScan
  openScan = scanId
  return entering
}

/** The navigation state a Recently opened link carries, so opening it does not move it. */
export const FROM_RECENT = { via: 'recently-opened' } as const

export function cameFromRecent(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as { via?: unknown }).via === FROM_RECENT.via
}

/** Mark a remembered scan as gone (deleted permanently, or answered 404). */
export function markGone(key: string | null, scanId: string) {
  if (!key) return
  const state = readRecent(key)
  if (!state.items.some((i) => i.id === scanId && i.status !== 'deleted')) return
  write(key, { ...state, items: state.items.map((i) => (i.id === scanId ? { ...i, status: 'deleted' } : i)) })
}

export function removeRecent(key: string | null, scanId: string) {
  if (!key) return
  const state = readRecent(key)
  if (!state.items.some((i) => i.id === scanId)) return
  write(key, { ...state, items: state.items.filter((i) => i.id !== scanId) })
}

/** The list for this user, re-read whenever it changes (in this tab or another). */
export function useRecentScans(key: string | null): RecentState {
  const raw = useSyncExternalStore(subscribe, () => readRaw(key), () => null)
  return useMemo(() => parse(raw), [raw])
}
